import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { getEnv } from '@/utils/helpers'

/**
 * One downloadable package in the connector release manifest. `sha256` lets an
 * installer script verify the download; the browser download path cannot.
 */
export interface ConnectorPackage {
  url: string
  sha256: string
}

/**
 * macOS ships a signed `.pkg` plus a plain `.zip` of the app bundle, for sites
 * where the installer package is blocked.
 */
export interface ConnectorMacPackage extends ConnectorPackage {
  zipUrl?: string
  zipSha256?: string
}

/**
 * Release manifest published to S3 by `build_and_upload.sh` (Windows) and
 * `installer/macos/publish-release.sh` (macOS). Each publisher merges only its
 * own platform key, so a platform is absent until it has been released.
 */
export interface ConnectorManifest {
  channel?: string
  version: string
  windows?: ConnectorPackage
  macos?: ConnectorMacPackage
}

const baseUrl = getEnv('VITE_CONNECTOR_MANIFEST_URL_BASE')

export const connectorApi = createApi({
  reducerPath: 'connectorApi',
  // Keep anonymous (S3 bucket serves the connector release prefix publicly).
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    getConnectorManifest: builder.query<ConnectorManifest, void>({
      query: () => ({ url: '/manifest.json' }),
    }),
  }),
})

export const { useGetConnectorManifestQuery } = connectorApi
